import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import "react-photo-view/dist/react-photo-view.css";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import ReservationInvoiceModal from "../modals/ReservationInvoiceModal";
import ReservationDetailsPage from "./ReservationDetailsPage";
import { useToast } from "../../../../contexts/ToastContext";
import { cleanupPaymentUrlParams } from "../../lib/reservationPayment";
import { canRefreshReservationInvoice, refreshReservationInvoice } from "../../lib/refreshInvoiceApi";
import {
    notifyRejectReservationError,
    notifyRejectReservationSuccess,
} from "./utils/rejectReservationToasts";

function ResDetails({ match }) {
    const [resDetails, setResDetails] = useState();
    const { t } = useTranslation(["reservations"]);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [isRefreshingInvoice, setIsRefreshingInvoice] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [isOpenExtraChargeModal, setIsOpenExtraChargeModal] = useState(false);
    const toast = useToast();
    const [stepper, setStepper] = useState(0);
    const [insurancePolicyData, setInsurancePolicyData] = useState(null);
    const [isInsuranceLoading, setIsInsuranceLoading] = useState(false);
    const [customerDetails, setCustomerDetails] = useState();
    const [carDetails, setCarDetails] = useState();
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [isPaymentOptionOpen, setIsPaymentOptionOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchInsurancePolicies = useCallback(
        async (axleInsurance) => {
            try {
                setIsInsuranceLoading(true);
                if (axleInsurance?.accessToken && axleInsurance?.policyId) {
                    const { data } = await apiClient.post("/api/insurance/get-axle-insurance-policies", {
                        accessToken: axleInsurance.accessToken,
                        policyId: axleInsurance.policyId,
                    });

                    if (data?.data) {
                        const policyData = data.data;
                        const isActive = policyData.isActive !== false;
                        const isExpired = policyData.expirationDate
                            ? new Date(policyData.expirationDate) < new Date(resDetails?.exactDropoffTime || new Date())
                            : false;

                        setInsurancePolicyData({
                            ...policyData,
                            isActive,
                            isExpired,
                        });
                    }
                }
            } catch {
                // ignore
            } finally {
                setIsInsuranceLoading(false);
            }
        },
        [resDetails?.exactDropoffTime],
    );

    const fetchAddons = useCallback(
        async (addonsInsuranceId) => {
            try {
                setIsInsuranceLoading(true);
                const { data } = await apiClient.get(`/api/addons-insurance/${addonsInsuranceId}`);

                if (data?.data) {
                    setInsurancePolicyData(data.data);
                }
            } catch {
                toast.error({
                    message: t("fetchAddonsError"),
                });
            } finally {
                setIsInsuranceLoading(false);
            }
        },
        [toast, t],
    );

    useEffect(() => {
        if (resDetails?.insuranceType === "axle" && resDetails?.axleInsurance) {
            fetchInsurancePolicies(resDetails.axleInsurance);
        } else if (["teqmobility", "bonzah", "abi"].includes(resDetails?.insuranceType?.toLowerCase())) {
            fetchAddons(resDetails.addonsInsuranceId);
        }
    }, [resDetails, fetchInsurancePolicies, fetchAddons]);

    const handleCarSwapSuccess = useCallback((updatedReservationDetails) => {
        setResDetails(updatedReservationDetails);
    }, []);

    useEffect(() => {
        if (!resDetails) return;

        const query = new URLSearchParams(window.location.search);
        const reservationId = query.get("resID");
        if (!reservationId) return;

        if (query.get("success") && reservationId && reservationId === match.params.id) {
            const updatePaymentStatus = async () => {
                try {
                    const amount = query.get("amount");

                    const resp = await apiClient.patch(
                        `api/${resDetails.flag ? "reservation" : "past-reservation"}/payment-status/${reservationId}`,
                        {
                            isPaid: true,
                            initialPayment: Number.parseInt(amount, 10),
                        },
                    );

                    if (resp?.data?.success) {
                        resDetails.isPaid = true;

                        toast.success({
                            message: t("paymentSuccess"),
                        });

                        const url = new URL(`${window.location.origin}/reservations/${match.params.id}`);
                        window.history.replaceState({}, "", url.toString());
                    }
                } catch {
                    toast.error({
                        message: t("resDetails.errorUpdatingPaymentStatus"),
                    });
                }
            };
            updatePaymentStatus();
        }

        if (query.get("cancelled")) {
            toast.error({
                message: t("paymentCancelled"),
            });

            const url = new URL(`${window.location.origin}/reservations/${match.params.id}`);
            window.history.replaceState({}, "", url.toString());
        }
    }, [match.params.id, toast, resDetails, t]);

    const fetchResDetails = useCallback(async () => {
        try {
            const res = await apiClient.get(`/api/reservation/${match?.params?.id}`);
            if (res?.data?.success) {
                setResDetails({ ...res.data?.data, flag: true });
            }
        } catch (_error) {
            if (_error?.response?.data?.message === "Reservation not found") {
                try {
                    const res = await apiClient.get(`/api/past-reservation/${match?.params?.id}`);
                    if (res?.data?.success) {
                        setResDetails({ ...res.data?.data, flag: false });
                    }
                } catch {
                    // ignore
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [match?.params?.id]);

    useEffect(() => {
        setIsLoading(true);
        fetchResDetails();
    }, [fetchResDetails]);

    // Back from a Stripe checkout for a reservation extension. The webhook moves the drop-off and
    // re-prices the invoice, usually a moment after the redirect, so re-read a few times.
    const extensionRefetchTimers = useRef([]);
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (query.get("extensionPayment") !== "true") return;

        // Stripping the params first makes this run once, however often the deps change.
        const paid = query.get("paymentSuccess") === "true";
        cleanupPaymentUrlParams();

        if (!paid) {
            toast.error({ message: t("extendReservation.paymentReturn.cancelled") });
            return;
        }

        toast.success({
            message: t("extendReservation.paymentReturn.successTitle"),
            description: t("extendReservation.paymentReturn.successDescription"),
        });
        extensionRefetchTimers.current = [1500, 4000, 8000].map((delay) => window.setTimeout(fetchResDetails, delay));
    }, [fetchResDetails, toast, t]);
    useEffect(() => () => extensionRefetchTimers.current.forEach((timer) => window.clearTimeout(timer)), []);

    const overDueModalOpen = () => {
        setIsOpenExtraChargeModal(true);
    };

    const overDueModalClose = () => {
        setIsOpenExtraChargeModal(false);
    };

    const openPaymentOptionModal = useCallback(() => {
        setIsPaymentOptionOpen(true);
    }, []);

    const closePaymentOptionModal = useCallback(() => {
        setIsPaymentOptionOpen(false);
    }, []);

    useEffect(() => {
        const refundModal = document.getElementById("refundModalId");

        const handleWindowClick = (event) => {
            if (refundModal && event.target === refundModal) {
                refundModal.style.display = "none";
            }
        };

        window.addEventListener("click", handleWindowClick);
        return () => window.removeEventListener("click", handleWindowClick);
    }, []);

    const openRefundModal = useCallback(() => {
        setIsRefundModalOpen(true);
    }, []);

    const closeRefundModal = useCallback(() => {
        setIsRefundModalOpen(false);
    }, []);

    useEffect(() => {
        if (resDetails) {
            const fetchCar = async () => {
                if (resDetails.carLinked && resDetails.carLinked.length > 0) {
                    try {
                        const resp = await apiClient.get(`/api/car/${resDetails.carSelected?.VIN}`);
                        if (resp.data?.success) {
                            setCarDetails(resp.data.data);
                        }
                    } catch {
                        // ignore
                    }
                }
            };

            if (resDetails.isCarSelected) {
                fetchCar();
            }

            if (resDetails.userUID) {
                const fetchCustomerDetails = async () => {
                    try {
                        const customerResponse = await apiClient.get(`/api/user/${resDetails.userUID}`);
                        if (customerResponse?.data?.data) {
                            setCustomerDetails(customerResponse?.data?.data);
                        }
                    } catch {
                        // ignore
                    }
                };
                fetchCustomerDetails();
            }
        }
    }, [resDetails]);

    const rejectReservation = () => {
        Modal.confirm({
            title: t("resDetails.confirmRejectReservation"),
            okText: t("resDetails.yes"),
            cancelText: t("resDetails.no"),
            centered: true,
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setIsRejecting(true);
                    const res = await apiClient.post("/api/past-reservation/create", {
                        ...resDetails,
                        pickupLocation: resDetails?.pickupLocation?.id,
                        category: resDetails?.category?._id
                            ? String(resDetails.category._id)
                            : resDetails?.category,
                        isRejected: true,
                    });

                    if (res?.data?.success) {
                        setResDetails((prev) => ({
                            ...prev,
                            isRejected: true,
                            flag: false,
                        }));
                        notifyRejectReservationSuccess(toast, t);
                        return;
                    }

                    notifyRejectReservationError(toast, t);
                } catch (error) {
                    notifyRejectReservationError(toast, t, error);
                } finally {
                    setIsRejecting(false);
                }
            },
        });
    };

    const refreshInvoice = () => {
        Modal.confirm({
            title: t("invoice.refreshConfirmTitle"),
            content: t("invoice.refreshConfirmContent"),
            okText: t("invoice.refresh"),
            cancelText: t("cancel"),
            centered: true,
            onOk: async () => {
                try {
                    setIsRefreshingInvoice(true);
                    await refreshReservationInvoice(resDetails);
                    await fetchResDetails();
                    toast.success({ message: t("invoice.refreshSuccess") });
                } catch (error) {
                    toast.error({
                        message: t("invoice.refreshError"),
                        description: error?.response?.data?.message || error?.message,
                    });
                } finally {
                    setIsRefreshingInvoice(false);
                }
            },
        });
    };

    useEffect(() => {
        if (resDetails) {
            if (resDetails.isApproved === true && resDetails.isCarLinked === false) {
                setStepper(1);
            } else if (resDetails.isRejected === true) {
                setStepper(1);
            } else if (resDetails.isCarLinked === true && resDetails.isCarSelected === false) {
                setStepper(2);
            } else if (resDetails.isCarSelected === true && resDetails.isCarPickedUp === false) {
                setStepper(3);
            } else if (resDetails.isCarPickedUp === true && resDetails.isCarDroppedOff === false) {
                setStepper(5);
            } else if (resDetails.isCarDroppedOff === true && resDetails.isPaid === false) {
                setStepper(6);
            } else if (resDetails.isCarDroppedOff === true && resDetails.isPaid === true) {
                setStepper(7);
            } else setStepper(0);
        }
    }, [resDetails]);

    return (
        <>
            <ReservationDetailsPage
                t={t}
                match={match}
                isLoading={isLoading}
                resDetails={resDetails}
                setResDetails={setResDetails}
                customerDetails={customerDetails}
                carDetails={carDetails}
                insurancePolicyData={insurancePolicyData}
                isInsuranceLoading={isInsuranceLoading}
                fetchResDetails={fetchResDetails}
                onCarSwapSuccess={handleCarSwapSuccess}
                onOpenInvoice={() => setInvoiceOpen(true)}
                canRefreshInvoice={canRefreshReservationInvoice(resDetails)}
                onRefreshInvoice={refreshInvoice}
                isRefreshingInvoice={isRefreshingInvoice}
                openRefundModal={openRefundModal}
                isRefundModalOpen={isRefundModalOpen}
                closeRefundModal={closeRefundModal}
                openPaymentOptionModal={openPaymentOptionModal}
                closePaymentOptionModal={closePaymentOptionModal}
                isPaymentOptionOpen={isPaymentOptionOpen}
                overDueModalOpen={overDueModalOpen}
                overDueModalClose={overDueModalClose}
                isOpenExtraChargeModal={isOpenExtraChargeModal}
                apiClient={apiClient}
                isRejecting={isRejecting}
                setIsRejecting={setIsRejecting}
                rejectReservation={rejectReservation}
            />
            <ReservationInvoiceModal
                open={invoiceOpen}
                onClose={() => setInvoiceOpen(false)}
                resDetails={resDetails}
                customerDetails={customerDetails}
                carDetails={carDetails}
                insurancePolicyData={insurancePolicyData}
                stepper={stepper}
            />
        </>
    );
}

ResDetails.propTypes = {
    match: PropTypes.object,
};

export default ResDetails;
